import {
  ModalForm,
  ProFormDatePicker,
  ProFormDateTimePicker,
  ProFormField,
  ProFormSelect,
  ProFormText,
  ProFormTextArea,
} from '@ant-design/pro-components';
import { Button, Col, Form, message, Row } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import modalStyles from '@/modules/zy-crm/components/business-modal/style.module.less';
import { CrmTagSelector } from '@/modules/zy-crm/components/crm-tags';
import { useDuplicateNameCheck } from '@/modules/zy-crm/components/duplicate-name-check';
import { FormSectionDivider } from '@/modules/zy-crm/components/form-section-divider';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import {
  queryAllTargetReferrals,
  ReferralFormSection,
  syncTargetReferrals,
} from '@/modules/zy-crm/components/referral-form-section';
import {
  ResourceAreaSelector,
  type ResourceAreaValue,
} from '@/modules/zy-crm/components/resource-area-selector';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import { FOLLOW_UP_STATUS_OPTIONS } from '@/modules/zy-crm/utils/constants';
import { resolveCrmScopedViewModeFromPathname } from '@/modules/zy-crm/utils/crm-view';
import { formatCustomerLevelOption } from '@/modules/zy-crm/utils/customer-level';
import { createSelectedMemberAwareAdapter } from '@/modules/zy-crm/utils/member-picker-adapter';
import { getResponsiveModalWidth } from '@/modules/zy-crm/utils/modal-width';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import {
  fetchContactOptions,
  fetchCustomerLevelOptions,
  fetchSourceChannelOptions,
  mergeOptions as mergeRelationOptions,
  type RelationOption,
} from '@/modules/zy-crm/utils/relation-options';
import { queryTaxonomyTreeByIdentifier } from '@/services/mp/taxonomy';
import { getCurrentMemberOption } from '@/utils/currentMember';
import {
  type AreaCascaderOption,
  type AreaUuidPathMap,
  buildAreaCascaderData,
  flattenTaxonomyToOptions,
  type TaxonomyOption,
} from '@/utils/taxonomy';

const TAXONOMY_AREA = 'CnAdministrativeDivision';
const TAXONOMY_INDUSTRY = 'TonghuashunIndustryTaxonomy';
const TAXONOMY_CSRC_INDUSTRY =
  'ChinaSecuritiesRegulatoryCommissionIndustryTaxonomy';

import { updateContact } from '@/services/bff/contact.api';
import {
  createCustomer,
  queryCustomerDetail,
  updateCustomer,
} from '@/services/bff/customer.api';
import { replaceEntityTags } from '@/services/bff/taxonomy-management.api';
import type {
  CreateCustomerDto,
  CustomerItem,
  UpdateCustomerDto,
} from '@/services/bff/types/customer';
import type { ReferralItem } from '@/services/bff/types/referral';
import { CompanyEnrichmentToolbar } from './company-enrichment-toolbar';
import {
  type CustomerFormValues,
  normalizeCustomerPayload,
  sanitizeMemberUuid,
  toCustomerFormValues,
} from './payload';
import { useCompanyEnrichment } from './use-company-enrichment';

export { sanitizeMemberUuid } from './payload';

export type MemberOption = {
  label: string;
  value: string;
};

type CustomerEditModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width?: number | string;
  recordUuid?: string;
  currentMemberUuid: string | null;
  context?: CustomerEditContext;
  zIndex?: number;
  /** Called after each successful creation, including save-and-continue. */
  onCreated?: (record: CustomerItem) => void;
  onSuccess: (record?: CustomerItem) => void;
};

export type CustomerEditContext = {
  leadName?: string;
  mobile?: string;
  telephone?: string;
  qq?: string;
  wechat?: string;
  email?: string;
  areaUuid?: string;
  areas?: ResourceAreaValue[];
  remark?: string;
};

type UseCustomerEditorStateOptions = {
  editingRecord: CustomerItem | null;
  currentMemberUuid: string | null;
};

const fieldColProps = { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 };
const wideFieldColProps = { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 };
const MemberPickerField = MemberPicker as unknown as React.FC<any>;
const ContactEditModal = React.lazy(() =>
  import('@/modules/zy-crm/components/contact-edit-modal').then((module) => ({
    default: module.ContactEditModal,
  })),
);

function getCurrentPathname() {
  if (typeof window === 'undefined') {
    return '';
  }
  return (
    (window.location.pathname || '') +
    (window.location.pathname.endsWith('/overview')
      ? window.location.search || ''
      : '')
  );
}

export function useCustomerEditorState({
  editingRecord,
  currentMemberUuid,
}: UseCustomerEditorStateOptions) {
  const {
    memberOptions,
    memberNameMap,
    loading: memberLoading,
    searchMembers,
    ensureMemberValue,
  } = useMemberDirectory();
  const [memberLoadFailed, setMemberLoadFailed] = useState(false);
  const [industryOptions, setIndustryOptions] = useState<TaxonomyOption[]>([]);
  const [csrcIndustryOptions, setCsrcIndustryOptions] = useState<
    TaxonomyOption[]
  >([]);
  const [sourceChannelOptions, setSourceChannelOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [customerLevelOptions, setCustomerLevelOptions] = useState<
    RelationOption[]
  >([]);
  const [areaOptions, setAreaOptions] = useState<AreaCascaderOption[]>([]);
  const [areaUuidPathMap, setAreaUuidPathMap] = useState<AreaUuidPathMap>({});
  const [relationLoading, setRelationLoading] = useState(false);

  const loadMembers = useCallback(
    async (keyword?: string) => {
      try {
        const currentUuid =
          editingRecord?.ownerUserUuid || currentMemberUuid || undefined;
        const options = await searchMembers(keyword, {
          ensureValue: currentUuid,
        });
        if (currentUuid) {
          await ensureMemberValue(currentUuid).catch(() => undefined);
        }
        setMemberLoadFailed(false);
        return options.map((item) => ({
          value: item.value,
          label: item.label,
        }));
      } catch {
        setMemberLoadFailed(true);
        message.error('成员列表加载失败，请稍后重试');
        return [] as MemberOption[];
      }
    },
    [
      currentMemberUuid,
      editingRecord?.ownerUserUuid,
      ensureMemberValue,
      searchMembers,
    ],
  );

  const loadRelationOptions = useCallback(
    async (_keyword?: string) => {
      setRelationLoading(true);
      try {
        const [
          areaTree,
          industryTree,
          csrcIndustryTree,
          sourceChannels,
          customerLevels,
        ] = await Promise.all([
          queryTaxonomyTreeByIdentifier(TAXONOMY_AREA),
          queryTaxonomyTreeByIdentifier(TAXONOMY_INDUSTRY),
          queryTaxonomyTreeByIdentifier(TAXONOMY_CSRC_INDUSTRY),
          fetchSourceChannelOptions(),
          fetchCustomerLevelOptions(),
        ]);

        const areaTreeData = buildAreaCascaderData(areaTree.tags);
        const flatIndustries = flattenTaxonomyToOptions(industryTree.tags);
        const flatCsrcIndustries = flattenTaxonomyToOptions(
          csrcIndustryTree.tags,
        );

        const resolvedIndustryOptions = [...flatIndustries];
        const currentIndustryUuid = editingRecord?.industryUuid;
        if (
          currentIndustryUuid &&
          !resolvedIndustryOptions.find((o) => o.value === currentIndustryUuid)
        ) {
          const currentIndustryName = editingRecord?.industryName;
          resolvedIndustryOptions.unshift({
            value: currentIndustryUuid,
            label: currentIndustryName || currentIndustryUuid,
          });
        }
        setIndustryOptions(resolvedIndustryOptions);

        const resolvedCsrcIndustryOptions = [...flatCsrcIndustries];
        const currentCsrcIndustryUuid = editingRecord?.csrcIndustryUuid;
        if (
          currentCsrcIndustryUuid &&
          !resolvedCsrcIndustryOptions.find(
            (o) => o.value === currentCsrcIndustryUuid,
          )
        ) {
          const currentCsrcIndustryName = editingRecord?.csrcIndustryName;
          resolvedCsrcIndustryOptions.unshift({
            value: currentCsrcIndustryUuid,
            label: currentCsrcIndustryName || currentCsrcIndustryUuid,
          });
        }
        setAreaOptions(areaTreeData.options);
        setAreaUuidPathMap(areaTreeData.uuidPathMap);
        setCsrcIndustryOptions(resolvedCsrcIndustryOptions);
        setSourceChannelOptions(sourceChannels);
        setCustomerLevelOptions(customerLevels);

        return {
          industries: flatIndustries,
          csrcIndustries: flatCsrcIndustries,
        };
      } catch (err) {
        console.error('Failed to load taxonomy options:', err);
        return { industries: [], csrcIndustries: [] };
      } finally {
        setRelationLoading(false);
      }
    },
    [editingRecord],
  );

  const primeRecordSelections = useCallback(
    (record: CustomerItem | null) => {
      if (record?.ownerUserUuid) {
        void ensureMemberValue(record.ownerUserUuid as string).catch(
          () => undefined,
        );
      }
      setIndustryOptions((prev) =>
        mergeRelationOptions(prev, [], record?.industryUuid),
      );
      setCsrcIndustryOptions((prev) =>
        mergeRelationOptions(prev, [], record?.csrcIndustryUuid),
      );
    },
    [ensureMemberValue],
  );

  return {
    memberOptions,
    memberNameMap,
    memberLoading,
    memberLoadFailed,
    industryOptions,
    csrcIndustryOptions,
    areaOptions,
    areaUuidPathMap,
    customerLevelOptions,
    sourceChannelOptions,
    relationLoading,
    loadMembers,
    loadRelationOptions,
    primeRecordSelections,
  };
}

const DEFAULT_CUSTOMER_MODAL_WIDTH = getResponsiveModalWidth('wideForm');

export function CustomerEditModal(props: CustomerEditModalProps) {
  const {
    open,
    onOpenChange,
    width = DEFAULT_CUSTOMER_MODAL_WIDTH,
    recordUuid,
    currentMemberUuid,
    context,
    zIndex,
    onCreated,
    onSuccess,
  } = props;
  const pageViewMode = resolveCrmScopedViewModeFromPathname(
    getCurrentPathname(),
    'customers',
    { allowPool: true },
  );
  const [form] = Form.useForm<CustomerFormValues>();
  const duplicateCheck = useDuplicateNameCheck({
    entity: 'customer',
    form,
    enabled: open && !recordUuid,
  });
  const enrichment = useCompanyEnrichment({
    form,
    open,
    recordUuid,
    viewMode: pageViewMode,
  });
  const saveAndContinueRef = useRef(false);
  const [editingRecord, setEditingRecord] = useState<CustomerItem | null>(null);
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadFailed, setDetailLoadFailed] = useState(false);
  const [initialReferrals, setInitialReferrals] = useState<ReferralItem[]>([]);
  const [contactOptions, setContactOptions] = useState<
    Array<{ label: string; value: string; raw?: Record<string, unknown> }>
  >([]);
  const [initialContactUuids, setInitialContactUuids] = useState<string[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const contactRequestRef = useRef(0);

  useEffect(() => {
    return () => {
      contactRequestRef.current += 1;
    };
  }, [open, recordUuid]);
  const {
    memberOptions,
    memberNameMap,
    memberLoadFailed,
    industryOptions,
    csrcIndustryOptions,
    areaOptions,
    areaUuidPathMap,
    customerLevelOptions,
    sourceChannelOptions,
    relationLoading,
    loadMembers,
    loadRelationOptions,
    primeRecordSelections,
  } = useCustomerEditorState({
    editingRecord,
    currentMemberUuid,
  });
  const currentOwnerUuid =
    sanitizeMemberUuid(editingRecord?.ownerUserUuid) ||
    currentMemberUuid ||
    undefined;
  const currentMemberOption = getCurrentMemberOption();
  const currentOwnerSessionName =
    currentMemberOption?.value === currentOwnerUuid
      ? currentMemberOption?.label
      : undefined;
  const currentOwnerName = currentOwnerUuid
    ? [
        memberOptions.find((item) => item.value === currentOwnerUuid)?.label,
        memberNameMap[currentOwnerUuid],
        editingRecord?.ownerUserName,
        editingRecord?.ownerName,
        editingRecord?.ownerMemberName,
        currentOwnerSessionName,
      ]
        .map((value) => String(value || '').trim())
        .find((value) => value && value !== currentOwnerUuid)
    : undefined;
  const ownerMemberAdapter = useMemo(
    () =>
      createSelectedMemberAwareAdapter(
        orgDirectoryAdapter,
        currentOwnerUuid,
        currentOwnerName,
      ),
    [currentOwnerName, currentOwnerUuid],
  );
  const currentOwnerLabel = currentOwnerUuid
    ? currentOwnerName || currentOwnerUuid
    : 'empty';
  const sourceChannelSelectKey = `${editingRecord?.sourceChannel || 'empty'}-${
    sourceChannelOptions.find(
      (item) => item.value === editingRecord?.sourceChannel,
    )?.label || 'pending'
  }`;
  const customerLevelSelectKey = `${
    editingRecord?.customerLevel === undefined ||
    editingRecord?.customerLevel === null
      ? 'empty'
      : String(editingRecord.customerLevel)
  }-${
    customerLevelOptions.find(
      (item) =>
        item.value ===
        (editingRecord?.customerLevel === undefined ||
        editingRecord?.customerLevel === null
          ? ''
          : String(editingRecord.customerLevel)),
    )?.label || 'pending'
  }`;

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setEditingRecord(null);
      setContactCreateOpen(false);
      setDetailLoading(false);
      setDetailLoadFailed(false);
      setInitialReferrals([]);
      setContactOptions([]);
      setInitialContactUuids([]);
      return;
    }
    let disposed = false;

    const loadRecord = async () => {
      if (!recordUuid) {
        setEditingRecord(null);
        setDetailLoading(false);
        setDetailLoadFailed(false);
        setInitialReferrals([]);
        return;
      }
      setDetailLoading(true);
      try {
        const [record, referrals, linkedContacts] = await Promise.all([
          queryCustomerDetail(recordUuid, { viewMode: pageViewMode }),
          queryAllTargetReferrals({
            targetType: 'customer',
            targetUuid: recordUuid,
          }),
          fetchContactOptions(undefined, recordUuid).catch(() => []),
        ]);
        if (disposed) {
          return;
        }
        setEditingRecord(record);
        setInitialReferrals(referrals);
        setContactOptions(linkedContacts);
        setInitialContactUuids(linkedContacts.map((item) => item.value));
        setDetailLoadFailed(false);
      } catch {
        if (disposed) {
          return;
        }
        setDetailLoadFailed(true);
        message.error('客户详情加载失败，请稍后重试');
      } finally {
        if (!disposed) {
          setDetailLoading(false);
        }
      }
    };

    void loadRecord();
    return () => {
      disposed = true;
    };
  }, [form, open, pageViewMode, recordUuid]);

  const loadContactOptions = useCallback(
    async (keyword?: string) => {
      const requestId = ++contactRequestRef.current;
      setContactLoading(true);
      try {
        const options = await fetchContactOptions(keyword);
        if (requestId !== contactRequestRef.current) return [];
        const available = options.filter((option) => {
          const customerUuid = String(option.raw?.customerUuid || '').trim();
          return !customerUuid || customerUuid === recordUuid;
        });
        setContactOptions((current) => {
          const selected = new Set<string>(
            form.getFieldValue('contactUuids') || [],
          );
          const merged = new Map(
            current
              .filter((item) => selected.has(item.value))
              .map((item) => [item.value, item]),
          );
          available.forEach((item) => {
            merged.set(item.value, item);
          });
          return Array.from(merged.values());
        });
        return available;
      } catch {
        if (requestId === contactRequestRef.current) {
          message.error('联系人列表加载失败，请稍后重试');
        }
        return [];
      } finally {
        if (requestId === contactRequestRef.current) setContactLoading(false);
      }
    },
    [form, recordUuid],
  );

  const createContextValues = useMemo<Partial<CustomerFormValues>>(
    () => ({
      name: context?.leadName,
      mobile: context?.mobile,
      telephone: context?.telephone,
      qq: context?.qq,
      wechat: context?.wechat,
      email: context?.email,
      areas:
        context?.areas ||
        (context?.areaUuid
          ? [{ areaUuid: context.areaUuid, isPrimary: true, sortOrder: 0 }]
          : undefined),
      remark: context?.remark,
    }),
    [
      context?.email,
      context?.areaUuid,
      context?.areas,
      context?.leadName,
      context?.mobile,
      context?.qq,
      context?.remark,
      context?.telephone,
      context?.wechat,
    ],
  );

  useEffect(() => {
    if (
      !open ||
      detailLoading ||
      detailLoadFailed ||
      (recordUuid && !editingRecord)
    ) {
      return;
    }
    form.resetFields();
    form.setFieldsValue({
      ...toCustomerFormValues(editingRecord, currentMemberUuid),
      contactUuids: initialContactUuids,
      ...(recordUuid ? {} : createContextValues),
    });
    primeRecordSelections(editingRecord);
    void loadMembers();
    void loadRelationOptions();
    void loadContactOptions();
  }, [
    currentMemberUuid,
    createContextValues,
    detailLoadFailed,
    detailLoading,
    editingRecord,
    form,
    initialContactUuids,
    loadMembers,
    loadRelationOptions,
    loadContactOptions,
    open,
    primeRecordSelections,
    recordUuid,
  ]);

  useEffect(() => {
    if (!open || !currentOwnerUuid || currentOwnerLabel === currentOwnerUuid) {
      return;
    }
    form.setFieldsValue({ ownerUserUuid: currentOwnerUuid });
  }, [currentOwnerLabel, currentOwnerUuid, form, open]);

  useEffect(() => {
    if (!open || !editingRecord) {
      return;
    }
    const nextFields: Partial<CustomerFormValues> = {};
    const sourceChannelValue = String(editingRecord.sourceChannel || '').trim();
    if (
      sourceChannelValue &&
      sourceChannelOptions.some((item) => item.value === sourceChannelValue)
    ) {
      nextFields.sourceChannel = sourceChannelValue;
    }
    const customerLevelValue =
      editingRecord.customerLevel === undefined ||
      editingRecord.customerLevel === null
        ? ''
        : String(editingRecord.customerLevel);
    if (
      customerLevelValue &&
      customerLevelOptions.some((item) => item.value === customerLevelValue)
    ) {
      nextFields.customerLevel = customerLevelValue;
    }
    if (Object.keys(nextFields).length > 0) {
      form.setFieldsValue(nextFields);
    }
  }, [customerLevelOptions, editingRecord, form, open, sourceChannelOptions]);

  return (
    <ModalForm<CustomerFormValues>
      className={modalStyles.formGrid}
      title={recordUuid ? '编辑客户' : '新建客户'}
      width={width}
      layout="vertical"
      form={form}
      onValuesChange={enrichment.onValuesChange}
      open={open}
      modalProps={{
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        className: `${modalStyles.modal} ${modalStyles.form}`,
        centered: true,
        destroyOnHidden: true,
        forceRender: true,
        onCancel: () => onOpenChange(false),
        zIndex,
        styles: {
          body: {
            overflowY: 'auto',
          },
        },
      }}
      onOpenChange={onOpenChange}
      submitter={{
        searchConfig: { submitText: '保存' },
        submitButtonProps: {
          disabled: duplicateCheck.busy || memberLoadFailed || detailLoadFailed,
          onClick: () => {
            saveAndContinueRef.current = false;
          },
        },
        render: (_, dom) => [
          dom[0],
          !recordUuid ? (
            <Button
              key="save-and-continue"
              disabled={
                duplicateCheck.busy || memberLoadFailed || detailLoadFailed
              }
              onClick={() => {
                saveAndContinueRef.current = true;
                form.submit();
              }}
            >
              保存并继续新增
            </Button>
          ) : null,
          dom[1],
        ],
      }}
      onFinishFailed={() => {
        saveAndContinueRef.current = false;
      }}
      onFinish={(values) =>
        duplicateCheck.submit(values, async () => {
          if (detailLoadFailed) {
            message.error('客户详情加载失败，无法提交，请先重试');
            return false;
          }
          if (memberLoadFailed) {
            message.error('成员列表加载失败，无法提交，请先重试');
            return false;
          }
          if (!recordUuid && !currentMemberUuid) {
            message.warning('当前会话缺少 memberUuid，无法创建客户');
            return false;
          }

          const payload = normalizeCustomerPayload(values);
          let savedRecord: CustomerItem | undefined;
          if (recordUuid) {
            savedRecord = await updateCustomer(
              recordUuid,
              payload as UpdateCustomerDto,
            );
          } else {
            const ownerUserUuid =
              sanitizeMemberUuid(values.ownerUserUuid) ||
              currentMemberUuid ||
              undefined;
            const createPayload = {
              ...payload,
              ownerUserUuid,
            } as CreateCustomerDto;
            savedRecord = await createCustomer(createPayload);
          }
          const selectedContactUuids = values.contactUuids || [];
          const removedContactUuids = initialContactUuids.filter(
            (contactUuid) => !selectedContactUuids.includes(contactUuid),
          );
          await Promise.all([
            syncTargetReferrals({
              targetType: 'customer',
              targetUuid: savedRecord.uuid,
              values: form.getFieldValue('referrals') || values.referrals,
              initialItems: initialReferrals,
            }),
            ...selectedContactUuids.map((contactUuid) =>
              updateContact(contactUuid, { customerUuid: savedRecord.uuid }),
            ),
            ...removedContactUuids.map((contactUuid) =>
              updateContact(contactUuid, { customerUuid: '' }),
            ),
          ]);
          try {
            await replaceEntityTags(
              'customer',
              savedRecord.uuid,
              values.tagUuids || [],
            );
          } catch {
            message.warning('客户主体已保存、标签未保存，请重新打开后重试');
          }
          if (!recordUuid) onCreated?.(savedRecord);
          message.success(recordUuid ? '更新成功' : '创建成功');
          if (!recordUuid && saveAndContinueRef.current) {
            saveAndContinueRef.current = false;
            enrichment.invalidate();
            form.resetFields();
            form.setFieldsValue({
              ...toCustomerFormValues(null, currentMemberUuid),
              ...createContextValues,
            });
            setInitialReferrals([]);
            setInitialContactUuids([]);
            setContactOptions((items) =>
              items.filter(
                (item) => !selectedContactUuids.includes(item.value),
              ),
            );
            return false;
          }
          onSuccess(savedRecord);
          return true;
        })
      }
    >
      <CompanyEnrichmentToolbar
        loading={enrichment.loading}
        feedback={enrichment.feedback}
        onConfirm={enrichment.confirm}
        onSelectionChange={enrichment.changeSelection}
        onDismiss={enrichment.invalidate}
        disabled={detailLoading || detailLoadFailed || !open}
        onSupplement={() => void enrichment.supplement()}
      />
      <FormSectionDivider title="常用信息" style={{ marginTop: 4 }} />
      <Row gutter={[12, 0]}>
        <Col data-crm-field="medium" {...fieldColProps}>
          <ProFormText name="name" label="客户名称" />
          {duplicateCheck.hint}
        </Col>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormField
            key={`owner-${currentOwnerUuid || 'empty'}-${currentOwnerLabel}`}
            name="ownerUserUuid"
            label="负责人"
            valueType="text"
          >
            <MemberPickerField
              adapter={ownerMemberAdapter}
              disabled={memberLoadFailed || detailLoading || detailLoadFailed}
            />
          </ProFormField>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="full" {...wideFieldColProps}>
          <ProFormTextArea
            name="remark"
            label="描述"
            fieldProps={{ rows: 4, maxLength: 500, showCount: true }}
          />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="medium" {...fieldColProps}>
          <ProFormText name="website" label="官网" />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="full" {...wideFieldColProps}>
          <Form.Item name="tagUuids" label="标签">
            <CrmTagSelector entityType="customer" canCreate />
          </Form.Item>
        </Col>
      </Row>

      <FormSectionDivider title="工商信息" />
      <Row gutter={[12, 0]}>
        <Col data-crm-field="full" {...wideFieldColProps}>
          <ProFormText
            name="fullName"
            label="客户全称"
            fieldProps={{ maxLength: 255 }}
          />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="medium" {...fieldColProps}>
          <ProFormText
            name="unifiedSocialCreditCode"
            label="统一社会信用代码"
            fieldProps={{ maxLength: 18 }}
            rules={[
              {
                pattern: /^[0-9A-HJ-NPQRTUWXY]{18}$/i,
                message: '请输入正确的18位统一社会信用代码',
              },
            ]}
          />
        </Col>
        <Col data-crm-field="medium" {...fieldColProps}>
          <ProFormText
            name="legalRepresentative"
            label="法定代表人"
            fieldProps={{ maxLength: 100 }}
          />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="medium" {...fieldColProps}>
          <ProFormText
            name="registeredCapital"
            label="注册资本"
            fieldProps={{ maxLength: 100 }}
          />
        </Col>
        <Col data-crm-field="medium" {...fieldColProps}>
          <ProFormDatePicker
            name="establishmentDate"
            label="成立时间"
            fieldProps={{ style: { width: '100%' } }}
          />
        </Col>
      </Row>

      <Row gutter={[12, 0]}>
        {(
          [
            ['qichachaUrl', '企查查网址', 'qcc.com'],
            ['tianyanchaUrl', '天眼查网址', 'tianyancha.com'],
          ] as const
        ).map(([name, label, domain]) => (
          <Col key={name} data-crm-field="medium" {...fieldColProps}>
            <ProFormText
              name={name}
              label={label}
              fieldProps={{ maxLength: 1000 }}
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || !String(value).trim()) return;
                    try {
                      const url = new URL(String(value).trim());
                      if (
                        ['http:', 'https:'].includes(url.protocol) &&
                        !url.username &&
                        !url.password &&
                        (!url.port || ['80', '443'].includes(url.port)) &&
                        (url.hostname === domain ||
                          url.hostname.endsWith(`.${domain}`))
                      )
                        return;
                    } catch {
                      /* Show the field validation message below. */
                    }
                    throw new Error(
                      `请输入 ${domain} 域名下的 HTTP/HTTPS 链接`,
                    );
                  },
                },
              ]}
            />
          </Col>
        ))}
      </Row>

      <FormSectionDivider title="跟进信息" />
      <Row gutter={[12, 0]}>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormSelect
            name="followUpStatus"
            label="跟进状态"
            options={FOLLOW_UP_STATUS_OPTIONS}
            rules={[{ required: true, message: '请选择跟进状态' }]}
          />
        </Col>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormDateTimePicker
            name="ownerTime"
            label="归属时间"
            rules={[{ required: true, message: '请选择归属时间' }]}
            fieldProps={{ style: { width: '100%' } }}
          />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormSelect
            name="lockStatus"
            label="锁定状态"
            options={[
              { label: '是', value: 'true' },
              { label: '否', value: 'false' },
            ]}
            rules={[{ required: true, message: '请选择锁定状态' }]}
          />
        </Col>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormSelect
            name="dealStatus"
            label="成交状态"
            options={[
              { label: '是', value: 'true' },
              { label: '否', value: 'false' },
            ]}
            rules={[{ required: true, message: '请选择成交状态' }]}
          />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormDatePicker
            name="contactLastTime"
            label="最近联系时间"
            fieldProps={{ style: { width: '100%' } }}
          />
        </Col>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormDatePicker
            name="contactNextTime"
            label="下次联系时间"
            fieldProps={{ style: { width: '100%' } }}
          />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="full" {...wideFieldColProps}>
          <ProFormTextArea
            name="contactLastContent"
            label="最近联系内容"
            fieldProps={{ rows: 4, maxLength: 300, showCount: true }}
          />
        </Col>
      </Row>

      <FormSectionDivider title="联系方式" />
      <Row gutter={[12, 0]}>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormText name="mobile" label="手机号" />
        </Col>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormText name="telephone" label="电话" />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormText name="email" label="邮箱" />
        </Col>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormText name="qq" label="QQ" />
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormText name="wechat" label="微信" />
        </Col>
      </Row>

      <FormSectionDivider title="业务信息" />
      <Row gutter={[12, 0]}>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormSelect
            key={sourceChannelSelectKey}
            name="sourceChannel"
            label="来源渠道"
            fieldProps={{
              options: sourceChannelOptions,
              showSearch: true,
              filterOption: (input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase()),
              optionFilterProp: 'label',
              allowClear: true,
            }}
          />
        </Col>
        <Col data-crm-field="short" {...fieldColProps}>
          <ProFormSelect
            key={customerLevelSelectKey}
            name="customerLevel"
            label="客户等级"
            fieldProps={{
              options: customerLevelOptions.map((item) => ({
                ...item,
                label: formatCustomerLevelOption(item),
              })),
              loading: relationLoading,
              showSearch: true,
              filterOption: (input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase()),
              optionFilterProp: 'label',
              allowClear: true,
            }}
          />
        </Col>
      </Row>

      <Row gutter={[12, 0]}>
        <Col data-crm-field="full" {...wideFieldColProps}>
          <Form.Item name="areas" label="区域">
            <ResourceAreaSelector
              options={areaOptions}
              uuidPathMap={areaUuidPathMap}
              disabled={relationLoading}
              placeholder="请选择区域"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[12, 0]}>
        <Col data-crm-field="medium" {...fieldColProps}>
          <ProFormSelect
            name="csrcIndustryUuid"
            label="证监会行业"
            fieldProps={{
              options: csrcIndustryOptions,
              loading: relationLoading,
              showSearch: true,
              filterOption: (input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase()),
              optionFilterProp: 'label',
              allowClear: true,
            }}
          />
        </Col>
        <Col data-crm-field="medium" {...fieldColProps}>
          <ProFormSelect
            name="industryUuid"
            label="同花顺行业"
            fieldProps={{
              options: industryOptions,
              loading: relationLoading,
              showSearch: true,
              filterOption: (input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase()),
              optionFilterProp: 'label',
              allowClear: true,
            }}
          />
        </Col>
      </Row>

      <FormSectionDivider title="联系人信息" />
      <ProFormSelect
        name="contactUuids"
        label="关联已有联系人"
        options={contactOptions}
        fieldProps={{
          mode: 'multiple',
          showSearch: true,
          allowClear: true,
          loading: contactLoading,
          filterOption: false,
          onSearch: (keyword) => void loadContactOptions(keyword),
          placeholder: '搜索并选择未归属客户的联系人',
        }}
        extra={
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => setContactCreateOpen(true)}
          >
            新建联系人
          </Button>
        }
      />

      <FormSectionDivider title="介绍人关系" />
      <ReferralFormSection
        form={form}
        name="referrals"
        title=""
        initialItems={initialReferrals}
      />
      {contactCreateOpen ? (
        <React.Suspense fallback={null}>
          <ContactEditModal
            open
            onOpenChange={setContactCreateOpen}
            width={width}
            zIndex={zIndex === undefined ? undefined : zIndex + 10}
            currentMemberUuid={currentMemberUuid}
            onSuccess={(contact) => {
              setContactCreateOpen(false);
              const contactUuid = String(contact?.uuid || '').trim();
              if (!contactUuid) {
                return;
              }
              const contactName = String(contact?.name || contactUuid).trim();
              setContactOptions((items) => [
                { value: contactUuid, label: contactName || contactUuid },
                ...items.filter((item) => item.value !== contactUuid),
              ]);
              const selectedContactUuids =
                form.getFieldValue('contactUuids') || [];
              form.setFieldsValue({
                contactUuids: Array.from(
                  new Set([...selectedContactUuids, contactUuid]),
                ),
              });
            }}
          />
        </React.Suspense>
      ) : null}
    </ModalForm>
  );
}
